import { useState, useEffect } from 'react';
import { EscortProfile } from '@/types';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/context/language-context';
import { generateMockProfiles } from './mockProfileData';

export const useProfilesApi = () => {
  const [profiles, setProfiles] = useState<EscortProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { t } = useLanguage();

  // Fetch profiles on component mount
  useEffect(() => {
    const fetchProfiles = async () => {
      console.log('useProfilesApi: Starting to fetch profiles');
      setIsLoading(true);
      try {
        console.log('Retrieving profiles from Supabase...');
        // Properly check authentication status
        const { data: authData } = await supabase.auth.getSession();
        console.log('Auth status:', authData.session ? 'Authenticated' : 'Not authenticated');
        
        // Log the URL being used (without exposing the full key)
        console.log('Using Supabase URL:', 'https://klsdmuldpwprmosyhxax.supabase.co');
        
        // Retrieve profiles from Supabase with expanded relations
        console.log('Querying escort_profiles table...');
        const { data: escortProfiles, error } = await supabase
          .from('escort_profiles')
          .select(`
            id,
            display_name,
            age,
            bio_short,
            bio_full,
            city,
            country,
            latitude,
            longitude,
            gender,
            height,
            weight,
            body_type,
            hair_color,
            eye_color,
            ethnicity,
            nationality,
            measurements,
            cup_size,
            intimate_area,
            has_tattoos,
            has_piercings,
            smoking,
            drinking,
            sexual_orientation,
            is_verified,
            is_online,
            is_touring,
            escort_services(*),
            escort_rates(*)
          `);
        
        if (error) {
          console.error('Error fetching from Supabase:', error);
          console.log('Error details:', JSON.stringify(error, null, 2));
          console.log('Falling back to mock profiles due to error');
          const mockProfiles = generateMockProfiles(true); // true pour mode urgence
          setProfiles(mockProfiles);
          setIsLoading(false);
          return;
        }

        console.log(`Loaded ${escortProfiles?.length || 0} profiles from Supabase`);
        if (escortProfiles && escortProfiles.length > 0) {
          console.log('First profile sample:', escortProfiles[0]);
          
          // Transform Supabase data to EscortProfile format
          const transformedProfiles: EscortProfile[] = escortProfiles.map(profile => ({
            id: profile.id,
            userId: profile.id,
            displayName: profile.display_name,
            age: profile.age,
            bio: {
              short: profile.bio_short || '',
              full: profile.bio_full || ''
            },
            location: {
              city: profile.city || '',
              country: profile.country || '',
              latitude: profile.latitude,
              longitude: profile.longitude
            },
            attributes: {
              gender: profile.gender,
              height: profile.height,
              weight: profile.weight,
              bodyType: profile.body_type,
              hairColor: profile.hair_color,
              eyeColor: profile.eye_color,
              ethnicity: profile.ethnicity,
              nationality: profile.nationality,
              measurements: profile.measurements,
              cupSize: profile.cup_size,
              intimateArea: profile.intimate_area,
              tattoos: profile.has_tattoos,
              piercings: profile.has_piercings,
              smoking: profile.smoking,
              drinking: profile.drinking,
              sexualOrientation: profile.sexual_orientation,
              languages: [] // Languages not yet managed in DB
            },
            services: Array.isArray(profile.escort_services) ? profile.escort_services.map(service => ({
              name: service.name,
              description: service.description || undefined,
              visibility: service.visibility as 'public' | 'teaser' | 'regular' | 'vip',
              status: service.status as 'included' | 'extra' | 'refused',
              price: service.price || undefined
            })) : [],
            rates: Array.isArray(profile.escort_rates) ? profile.escort_rates.map(rate => ({
              id: rate.id,
              category: rate.category || 'incall',
              description: rate.description,
              price: rate.price,
              currency: rate.currency,
              duration: rate.duration ? String(rate.duration) : undefined,
              visibility: rate.visibility as 'public' | 'teaser' | 'regular' | 'vip',
            })) : [],
            isVerified: profile.is_verified,
            isOnline: profile.is_online,
            isTouring: profile.is_touring,
            ai_chat_enabled: false, // Valeur par défaut car non présente dans la table escort_profiles
            ai_chat_response_mode: 'always', // Valeur par défaut car non présente dans la table escort_profiles
            ai_chat_schedule: { days: [], startTime: '', endTime: '', timezone: '' }, // Valeur par défaut car non présente dans la table escort_profiles
            photos: {
              main: '', // Set to empty or placeholder, let useProfileImages handle loading
              gallery: [] // Assuming gallery photos are loaded separately if needed
            },
            tourDates: [] // Tour dates not yet managed in DB
          }));

          console.log(`Transformed ${transformedProfiles.length} profiles from Supabase`);
          console.log('First transformed profile:', transformedProfiles[0]);
          setProfiles(transformedProfiles);
          setIsLoading(false);
          return;
        } else {
          console.log('No profiles found in Supabase.');
          
          // Try a simpler query to check if we can access the table at all
          const { data: countResult, error: countError } = await supabase
            .from('escort_profiles')
            .select('id', { count: 'exact' });
            
          if (countError) {
            console.error('Error counting profiles:', countError);
            // Aucun profil et erreur de comptage -> utiliser les profils fictifs en mode urgence
            console.log('Falling back to emergency mock profiles due to count error');
            const mockProfiles = generateMockProfiles(true);
            setProfiles(mockProfiles);
          } else {
            console.log(`Count query returned ${countResult?.length || 0} results`);
            // Aucun profil mais pas d'erreur -> utiliser les profils fictifs normaux
            console.log('Falling back to standard mock profiles as no real profiles exist');
            const mockProfiles = generateMockProfiles(false);
            setProfiles(mockProfiles);
          }
          
          setIsLoading(false);
          return;
        }
      } catch (error) {
        console.error('Error loading profiles:', error);
        toast({
          title: t('profileManagement.error'),
          description: t('profileManagement.profile_update_error'),
          variant: 'destructive',
        });
        
        // En cas d'erreur, utiliser les profils fictifs en mode urgence
        console.log('Using mock profiles due to error in try/catch block');
        const mockProfiles = generateMockProfiles(true);
        setProfiles(mockProfiles);
        setIsLoading(false);
      }
    };

    fetchProfiles();
  }, [toast, t]);

  return { profiles, isLoading };
};
